import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, type DepartmentRef, type ProjectRef } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n, type TranslationKey } from '../lib/i18n';
import { FormGrid, FormPage } from '../components/FormPage';
import { Field, Input, Select, Textarea, useToast } from '../components/ui';
import { AttachmentsField, useUploads } from '../components/Attachments';
import { NameField, hasDescription, nameFits } from '../components/NameField';
import { refusalText } from '../lib/reach';

const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];

type ClientOption = { id: string; code: string; companyName: string; companyNameAr: string | null };
type ContactOption = { id: string; name: string; jobTitle: string | null; deletedAt?: string | null };

/** What the form says when the API refuses the contact (`details.reason`). */
const CONTACT_REFUSALS: Record<string, TranslationKey> = {
  MISSING: 'tickets.contactRefusedMissing',
  REMOVED: 'tickets.contactRefusedRemoved',
  OTHER_CLIENT: 'tickets.contactRefusedOther',
  UNKNOWN: 'tickets.contactRefusedOther',
};

/**
 * Routes: `/tickets/new` and `/tickets/:id/edit`. A ticket can be edited until
 * it is started — while it is New or Assigned — by whoever raised it, a team
 * leader or an admin. Its department moves by rerouting, which keeps the SLA
 * history, and its files are added on its page, so the edit form has neither.
 *
 * Raising one is the administrators' and of whoever they give Create tickets
 * on the employee page (operator, 2026-10-07); anyone else who reaches the
 * New ticket page is told so, and nothing is sent.
 *
 * Every ticket we raise names the client and who at the client asked for it —
 * one of the client's contacts, both required (operator, 2026-10-09). A ticket
 * the client raised on the portal names nobody: the client asked itself.
 */
export function TicketFormPage() {
  const { id } = useParams();
  const editing = !!id;
  const { t, locale, localized } = useI18n();
  const { can, canAny } = useAuth();
  // Editing is the ticket's own rule (`can.edit`); creating needs tickets.create.
  const mayCreate = editing || can(P.TICKETS_CREATE);
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState({
    subject: '',
    description: '',
    clientId: '',
    contactId: '',
    targetDepartmentId: '',
    categoryId: '',
    projectId: '',
    priority: 'MEDIUM',
  });
  const uploads = useUploads();

  const existing = useQuery({
    queryKey: ['ticket', id],
    queryFn: () => api.get<any>(`/tickets/${id}`),
    enabled: editing,
  });
  const [loaded, setLoaded] = useState(false);
  if (editing && existing.data && !loaded) {
    setLoaded(true);
    const ticket = existing.data;
    setForm({
      subject: ticket.subject,
      description: ticket.description ?? '',
      clientId: ticket.clientId ?? '',
      contactId: ticket.contactId ?? '',
      targetDepartmentId: ticket.targetDepartmentId,
      categoryId: ticket.categoryId ?? '',
      projectId: ticket.projectId ?? '',
      priority: ticket.priority,
    });
  }
  const ticket = existing.data;
  // A ticket the client raised on the portal stays theirs, and names no contact.
  const clientFixed = editing && !!ticket?.requesterAccountId;
  const locked = editing && !!ticket && !ticket.can?.edit;

  // The departments' names, readable by whoever raises tickets or creates tasks
  // (`departments.read` or not: an Employee holds `tasks.create`, and given Create
  // tickets `tickets.create`, without it).
  const { data: departments } = useQuery({
    queryKey: ['departments', 'picker'],
    queryFn: () => api.get<DepartmentRef[]>('/departments/options'),
    enabled: !editing && mayCreate,
  });
  const { data: categories } = useQuery({
    queryKey: ['ticket-categories'],
    queryFn: () => api.get<any[]>('/ticket-categories'),
  });
  // Every ticket names its client (operator, 2026-10-03), and its project is one
  // of that client's — so the projects come once a client is picked.
  // Readable by whoever raises or handles tickets; someone editing a ticket they
  // raised before losing Create tickets keeps the client it names (`withCurrent`).
  const readsClients = canAny(P.CLIENTS_VIEW, P.TICKETS_CREATE, P.TICKETS_HANDLE);
  const { data: clients } = useQuery({
    queryKey: ['client-options'],
    queryFn: () => api.get<ClientOption[]>('/clients/options'),
    enabled: mayCreate && readsClients,
  });
  const { data: projects } = useQuery({
    queryKey: ['projects', 'options', form.clientId],
    queryFn: () => api.get<ProjectRef[]>('/projects/options', { clientId: form.clientId }),
    enabled: !!form.clientId,
  });
  // Who at the client asked for it: one of the client's contacts. Read again every
  // time this tab comes back into view — however fresh — so someone just added in
  // another tab is offered.
  const contactsQuery = useQuery({
    queryKey: ['client-contacts', form.clientId, 'options'],
    queryFn: () => api.get<ContactOption[]>(`/clients/${form.clientId}/contacts/options`),
    enabled: mayCreate && readsClients && !!form.clientId && !clientFixed,
    refetchOnWindowFocus: 'always',
  });
  const contacts = contactsQuery.data;

  // What the ticket already names stays on offer, even if the lists no longer
  // carry it (a client since made inactive, a project since finished, a contact
  // since removed from the client's list).
  const clientChoices = withCurrent(clients ?? [], editing ? ticket?.client : null);
  const projectChoices = withCurrent(
    projects ?? [],
    editing && ticket?.project && form.clientId === ticket.clientId ? ticket.project : null,
  );
  const contactChoices = withCurrent<ContactOption>(
    contacts ?? [],
    editing && ticket?.contact && form.clientId === ticket.clientId ? ticket.contact : null,
  );
  const pickedClient = clientChoices.find((c) => c.id === form.clientId);
  const clientName = pickedClient
    ? locale === 'ar' && pickedClient.companyNameAr
      ? pickedClient.companyNameAr
      : pickedClient.companyName
    : '';
  // The person who asked may not be on the client's list yet: whoever keeps the
  // client's contacts adds them in a new tab, and this form keeps what was typed.
  const addsContacts = can(P.CLIENT_CONTACTS_MANAGE) && can(P.CLIENTS_VIEW);
  const noContacts = contactsQuery.isSuccess && contactChoices.length === 0;

  const save = useMutation({
    mutationFn: () =>
      editing
        ? api.patch<any>(`/tickets/${id}`, {
            subject: form.subject,
            description: form.description,
            ...(clientFixed ? {} : { clientId: form.clientId, contactId: form.contactId }),
            projectId: form.projectId || null,
            categoryId: form.categoryId || null,
            priority: form.priority,
          })
        : api.post<any>('/tickets', {
            subject: form.subject,
            description: form.description,
            clientId: form.clientId,
            contactId: form.contactId,
            targetDepartmentId: form.targetDepartmentId,
            categoryId: form.categoryId || undefined,
            projectId: form.projectId || undefined,
            priority: form.priority,
            fileIds: uploads.fileIds.length ? uploads.fileIds : undefined,
          }),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
      if (editing) {
        void queryClient.invalidateQueries({ queryKey: ['ticket', id] });
        toast.success(t('tickets.saved'));
        // In place of the form, so going back from the ticket does not reopen it.
        navigate(`/tickets/${id}`, { replace: true });
      } else {
        toast.success(`${saved.number} — ${t('tickets.new')}`);
        navigate(`/tickets/${saved.id}`);
      }
    },
    onError: (e: any) => {
      // Create tickets taken away while the form was open.
      if (!editing && e?.code === 'PERMISSION_DENIED') return toast.error(t('tickets.createNotAllowed'));
      // The contact was removed, or the client changed, while the form was open.
      if (e?.details?.field === 'contactId') {
        void contactsQuery.refetch();
        return toast.error(t(CONTACT_REFUSALS[e.details.reason] ?? 'tickets.contactRefusedOther'));
      }
      // The project was completed while the form was open: it takes no new tickets.
      if (e?.code === 'PROJECT_CLOSED') void queryClient.invalidateQueries({ queryKey: ['projects', 'options'] });
      toast.error(refusalText(e, t));
    },
  });

  const addContactLink = (label: TranslationKey) => (
    <a
      href={`/clients/${form.clientId}/contacts/new`}
      target="_blank"
      rel="noopener"
      title={t('tickets.contactNewTab')}
      className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline"
    >
      {t(label)}
      <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
    </a>
  );

  let contactHint: React.ReactNode;
  if (clientFixed) contactHint = t('tickets.portalNoContact');
  else if (!form.clientId) contactHint = t('tickets.contactPickClient');
  else if (locked) contactHint = t('tickets.contactHint');
  else if (noContacts) {
    contactHint = (
      <span className="text-warning-fg dark:text-warning-dark">
        {t('tickets.contactNone', { client: clientName })}{' '}
        {addsContacts ? addContactLink('tickets.contactAddFirst') : t('tickets.contactAskAdmin')}
      </span>
    );
  } else {
    contactHint = (
      <>
        {t('tickets.contactHint')}{' '}
        {addsContacts ? addContactLink('tickets.contactNotListed') : t('tickets.contactAskAdmin')}
      </>
    );
  }

  return (
    <FormPage
      title={editing ? t('tickets.edit') : t('tickets.new')}
      subtitle={
        editing && ticket ? (locked ? ticket.number : `${ticket.number} — ${t('tickets.editableUntilStarted')}`) : undefined
      }
      backTo={editing ? `/tickets/${id}` : '/tickets'}
      backLabel={editing && ticket ? ticket.number : t('nav.tickets')}
      submitLabel={editing ? t('common.save') : t('common.create')}
      submitting={save.isPending}
      // Saving while a file is still uploading would leave it behind.
      disabled={
        !mayCreate ||
        locked ||
        !nameFits(form.subject) ||
        !hasDescription(form.description) ||
        !form.clientId ||
        (!clientFixed && !form.contactId) ||
        (!editing && !form.targetDepartmentId) ||
        uploads.busy
      }
      loading={editing && existing.isLoading}
      error={editing ? existing.error : undefined}
      onRetry={() => void existing.refetch()}
      onSubmit={() => save.mutate()}
    >
      <div className="space-y-4">
        {!mayCreate && (
          <p className="rounded-md border border-warning/35 bg-warning/8 px-3 py-2 text-body-sm text-warning-fg dark:text-warning-dark">
            {t('tickets.createNotAllowed')}
          </p>
        )}
        {locked && (
          <p className="rounded-md border border-warning/35 bg-warning/8 px-3 py-2 text-body-sm text-warning-fg dark:text-warning-dark">
            {['NEW', 'ASSIGNED'].includes(ticket.status) ? t('tickets.editNotYours') : t('tickets.startedLocked')}
          </p>
        )}

        <NameField
          label={t('tickets.subject')}
          value={form.subject}
          onChange={(subject) => setForm({ ...form, subject })}
          disabled={locked}
          autoFocus
        />

        <Field label={t('common.description')} required>
          <Textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            disabled={locked}
          />
        </Field>

        <FormGrid>
          <Field
            label={t('tickets.client')}
            required
            hint={clientFixed ? t('tickets.portalClientFixed') : t('tickets.clientHint')}
          >
            <Select
              value={form.clientId}
              onChange={(e) => setForm({ ...form, clientId: e.target.value, contactId: '', projectId: '' })}
              disabled={locked || clientFixed}
            >
              <option value="">—</option>
              {clientChoices.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} — {locale === 'ar' && c.companyNameAr ? c.companyNameAr : c.companyName}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t('tickets.contact')} required={!clientFixed} hint={contactHint}>
            <Select
              value={form.contactId}
              onChange={(e) => setForm({ ...form, contactId: e.target.value })}
              disabled={locked || clientFixed || !form.clientId}
            >
              <option value="">—</option>
              {contactChoices.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.jobTitle ? `${c.name} — ${c.jobTitle}` : c.name}
                  {c.deletedAt ? ` (${t('tickets.contactRemoved')})` : ''}
                </option>
              ))}
            </Select>
          </Field>

          {/* §9.8 — the picker only offers projects the requester may see, and of
              those only the client's own. */}
          <Field
            label={t('common.project')}
            hint={form.clientId ? t('tickets.clientProjectsOnly') : t('tickets.pickClientFirst')}
          >
            <Select
              value={form.projectId}
              onChange={(e) => setForm({ ...form, projectId: e.target.value })}
              disabled={locked || !form.clientId}
            >
              <option value="">{t('common.general')}</option>
              {projectChoices.map((p) => (
                <option key={p.id} value={p.id}>{localized(p)}</option>
              ))}
            </Select>
          </Field>

          {editing ? (
            <Field label={t('common.department')} hint={t('tickets.departmentByReroute')}>
              <Input value={ticket ? localized(ticket.targetDepartment) : ''} disabled readOnly />
            </Field>
          ) : (
            <Field label={t('tickets.targetDepartment')} required hint={t('tickets.slaStartsNote')}>
              <Select
                value={form.targetDepartmentId}
                onChange={(e) => setForm({ ...form, targetDepartmentId: e.target.value })}
              >
                <option value="">—</option>
                {(departments ?? []).map((d) => (
                  <option key={d.id} value={d.id}>{localized(d)}</option>
                ))}
              </Select>
            </Field>
          )}

          <Field label={t('tickets.category')}>
            <Select
              value={form.categoryId}
              onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
              disabled={locked}
            >
              <option value="">—</option>
              {(categories ?? []).map((c) => (
                <option key={c.id} value={c.id}>{localized(c)}</option>
              ))}
            </Select>
          </Field>

          <Field label={t('common.priority')}>
            <Select
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              disabled={locked}
            >
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>{enumLabel(p, locale)}</option>
              ))}
            </Select>
          </Field>
        </FormGrid>

        {!editing && <AttachmentsField uploads={uploads} />}
      </div>
    </FormPage>
  );
}

/** The options, with the record's current one first when the list has dropped it. */
export function withCurrent<T extends { id: string }>(options: T[], current: T | null | undefined): T[] {
  return current && !options.some((o) => o.id === current.id) ? [current, ...options] : options;
}
